import { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Globe, ArrowLeft } from 'lucide-react'
import { useWebsite } from '../../hooks/use-website'
import { BrandingContext } from '../../contexts/BrandingContext'
import { BlockRenderer } from '../website-blocks'
import ButlerWidget from '../butler-widget/ButlerWidget'
import PublicNavbar from './PublicNavbar'
import PublicFooter from './PublicFooter'
import { useI18n } from '../../i18n'
import { extractShortcode } from '../../helper/website-url'

export default function PublicWebsitePage() {
  const { shortcode: pathShortcode } = useParams<{ shortcode: string }>()
  const shortcode = pathShortcode || extractShortcode()
  const { fetchPublicWebsite, website } = useWebsite()
  const { lang, toggleLang, t } = useI18n()
  const [activeSlug, setActiveSlug] = useState('home')
  const [fetched, setFetched] = useState(false)
  const fetchedRef = useRef(false)

  useEffect(() => {
    if (shortcode && !fetchedRef.current) {
      fetchedRef.current = true
      fetchPublicWebsite(shortcode).then(() => setFetched(true))
    }
  }, [shortcode])

  useEffect(() => {
    if (website?.pages?.length) {
      const home = website.pages.find(p => p.slug === 'home') || website.pages[0]
      setActiveSlug(home.slug)
    }
  }, [website])

  // Not found
  if (!website && fetched) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6">
        <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
          <Globe className="h-8 w-8 text-gray-300" />
        </div>
        <h2 className="text-lg font-semibold text-[#1a1a1a] mb-2">{t('网站不存在或未发布', 'Website not found or unpublished')}</h2>
        <p className="text-sm text-gray-400 text-center max-w-md mb-6">{t('请检查链接是否正确', 'Please check if the link is correct')}</p>
        <Link to="/my-website"
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-[#1a1a1a] text-white text-sm font-medium hover:bg-[#333] transition-all">
          <ArrowLeft className="h-4 w-4" />
          {t('返回我的官网', 'Back to My Website')}
        </Link>
      </div>
    )
  }

  // Loading
  if (!website) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <div className="animate-pulse text-gray-300">Loading...</div>
      </div>
    )
  }

  const activePage = website.pages.find(p => p.slug === activeSlug && p.visible) || website.pages.find(p => p.visible)

  const slugMap: Record<string, string> = {}
  website.pages.forEach(p => { slugMap[p.id] = p.slug })

  // Collect visible blocks
  const visibleBlocks = activePage
    ? activePage.blocks
        .filter(b => b.visible)
        .sort((a, b) => a.blockOrder - b.blockOrder)
    : []

  return (
    <BrandingContext.Provider value={{ branding: website.branding, isEditing: false }}>
      <div className="min-h-screen flex flex-col bg-white">
        <PublicNavbar
          logoUrl={website.branding.logoUrl}
          companyName={website.companyName}
          primaryColor={website.branding.primaryColor}
          nav={website.nav.filter(n => {
            const page = website.pages.find(p => p.id === n.pageId)
            return page?.visible !== false
          })}
          activeSlug={activeSlug}
          onNavClick={(slug) => {
            if (slug) setActiveSlug(slug)
          }}
          slugMap={slugMap}
          lang={lang}
          onToggleLang={toggleLang}
        />

        {/* Main content area grows to fill space */}
        <main className="flex-1">
          {visibleBlocks.length > 0 && visibleBlocks.map(block => (
            <BlockRenderer key={block.id} block={block} />
          ))}

          {/* Show "under construction" if no blocks produced visible content */}
          {visibleBlocks.length === 0 && (
            <div className="flex flex-col items-center justify-center py-32 px-6">
              <div className="w-16 h-16 rounded-2xl bg-orange-500/10 flex items-center justify-center mb-4">
                <Globe className="h-8 w-8 text-orange-300" />
              </div>
              <h2 className="text-lg font-semibold text-[#1a1a1a] mb-2">{website.companyName}</h2>
              <p className="text-sm text-gray-400 text-center max-w-md">{t('官网内容正在建设中，请稍后再来访问', 'Website content is under construction, please visit again later')}</p>
            </div>
          )}
        </main>

        <PublicFooter companyName={website.companyName} primaryColor={website.branding.primaryColor} />

        {website.butler.enabled && (
          <ButlerWidget
            shortcode={website.shortcode}
            botName={website.butler.botName}
            avatarUrl={website.butler.avatarUrl}
            welcomeMessage={website.butler.welcomeMessage}
            position={website.butler.position}
          />
        )}
      </div>
    </BrandingContext.Provider>
  )
}
